import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import api from '../utils/axios';
import { useNavigate } from 'react-router-dom';
import {
    LineChart, Line, BarChart, Bar, PieChart, Pie,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';

const Analytics = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [chatOverview, setChatOverview] = useState(null);
    const [sourceStats, setSourceStats] = useState(null);
    const [dateRange, setDateRange] = useState({
        startDate: '',
        endDate: ''
    });
    const [showCustomRange, setShowCustomRange] = useState(false);
    const [chartData, setChartData] = useState(null);
    const [tenantId, setTenantId] = useState('');
    const [tenantIdInput, setTenantIdInput] = useState('');

    useEffect(() => {
        const fetchAnalytics = async () => {
            try {
                setLoading(true);

                const params = new URLSearchParams();
                if (dateRange.startDate) params.append('startDate', dateRange.startDate);
                if (dateRange.endDate) params.append('endDate', dateRange.endDate);
                if (tenantId) params.append('tenantId', tenantId);
                const queryString = params.toString();

                // Fetch all analytics data in parallel
                const [chatRes, sourceRes, chartRes] = await Promise.all([
                    api.get(`/api/analytics/chat-overview?${queryString}`),
                    api.get(`/api/analytics/source-stats?${queryString}`),
                    api.get(`/api/analytics/chart-data?${queryString}`)
                ]);

                setChatOverview(chatRes.data);
                setSourceStats(sourceRes.data);
                setChartData(chartRes.data);

                setLoading(false);
            } catch (err) {
                console.error('Error fetching analytics:', err);
                setError('Failed to load analytics data');
                setLoading(false);
            }
        };

        fetchAnalytics();
    }, [dateRange, tenantId]);

    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setDateRange(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleTenantFilter = (e) => {
        e.preventDefault();
        setTenantId(tenantIdInput.trim());
    };

    const clearTenantFilter = () => {
        setTenantIdInput('');
        setTenantId('');
    };

    const setPresetRange = (days) => {
        const end = new Date();
        const start = new Date();

        if (days === 0) {
            // Today
            start.setHours(0, 0, 0, 0);
        } else {
            // Last N days
            start.setDate(start.getDate() - days);
        }

        // Format to YYYY-MM-DD for input[type="date"]
        const formatDate = (date) => {
            return date.toISOString().split('T')[0];
        };

        setDateRange({
            startDate: formatDate(start),
            endDate: formatDate(end)
        });
        setShowCustomRange(false);
    };

    if (loading) {
        return (
            <div className="flex h-screen bg-gray-50">
                <Sidebar />
                <div className="flex-1 overflow-auto">
                    <div className="flex items-center justify-center h-full">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-screen bg-gray-50">
                <Sidebar />
                <div className="flex-1 overflow-auto">
                    <div className="p-6 text-center text-red-500">
                        <p>{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-screen bg-gray-50">
            <Sidebar />
            <div className="flex-1 overflow-auto">
                <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
                    <div className="flex justify-between items-center flex-wrap gap-4">
                        <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>

                        {/* Filters Row */}
                        <div className="flex flex-wrap items-center gap-4">
                            {/* Tenant / Pharmacie Filter */}
                            <form onSubmit={handleTenantFilter} className="flex items-center gap-2 bg-white rounded-lg shadow-sm border border-gray-100 px-3 py-1.5">
                                <svg className="w-4 h-4 text-indigo-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                <input
                                    type="text"
                                    value={tenantIdInput}
                                    onChange={e => setTenantIdInput(e.target.value)}
                                    placeholder="Filtrer par Tenant ID"
                                    className="text-sm border-none outline-none w-40 bg-transparent"
                                />
                                <button type="submit" className="text-xs font-medium text-indigo-600 hover:text-indigo-800 px-1">OK</button>
                                {tenantId && (
                                    <button type="button" onClick={clearTenantFilter} className="text-xs text-red-400 hover:text-red-600 font-medium">✕</button>
                                )}
                            </form>
                            {/* Preset Buttons */}
                            <div className="flex bg-white rounded-lg shadow-sm border border-gray-100 p-1">
                                <button
                                    onClick={() => setPresetRange(0)}
                                    className="px-3 py-1 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                                >
                                    Today
                                </button>
                                <button
                                    onClick={() => setPresetRange(7)}
                                    className="px-3 py-1 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                                >
                                    Last 7 Days
                                </button>
                                <button
                                    onClick={() => setPresetRange(30)}
                                    className="px-3 py-1 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                                >
                                    Last 30 Days
                                </button>
                                <button
                                    onClick={() => setShowCustomRange(!showCustomRange)}
                                    className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${showCustomRange ? 'bg-black text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                                >
                                    Custom
                                </button>
                            </div>

                            {showCustomRange && (
                                <div className="flex items-center gap-4 bg-white p-2 rounded-lg shadow-sm border border-gray-100">
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm text-gray-500">From:</label>
                                        <input
                                            type="date"
                                            name="startDate"
                                            value={dateRange.startDate}
                                            onChange={handleDateChange}
                                            className="border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-black"
                                        />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm text-gray-500">To:</label>
                                        <input
                                            type="date"
                                            name="endDate"
                                            value={dateRange.endDate}
                                            onChange={handleDateChange}
                                            className="border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-black"
                                        />
                                    </div>
                                    {(dateRange.startDate || dateRange.endDate) && (
                                        <button
                                            onClick={() => setDateRange({ startDate: '', endDate: '' })}
                                            className="text-xs text-red-500 hover:text-red-700 font-medium"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Active tenant filter banner */}
                    {tenantId && (
                        <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2 text-sm text-indigo-700">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                            </svg>
                            <span>Filtré par Tenant ID: <strong>{tenantId}</strong></span>
                            <button onClick={clearTenantFilter} className="ml-2 text-indigo-400 hover:text-indigo-700 font-bold">✕ Effacer</button>
                        </div>
                    )}

                    {/* Overview Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <h3 className="text-sm font-medium text-gray-500">Messages envoyés</h3>
                            <p className="text-3xl font-bold text-gray-900 mt-2">{chatOverview?.userSentMessages ?? chatOverview?.totalMessages ?? 0}</p>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <h3 className="text-sm font-medium text-gray-500">Total Users</h3>
                            <p className="text-3xl font-bold text-gray-900 mt-2">{chatOverview?.userCount || 0}</p>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <h3 className="text-sm font-medium text-gray-500">Total Sessions</h3>
                            <p className="text-3xl font-bold text-gray-900 mt-2">{chatOverview?.sessionCount || 0}</p>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <h3 className="text-sm font-medium text-gray-500">Avg Response Time</h3>
                            <p className="text-3xl font-bold text-gray-900 mt-2">{chatOverview?.responseTime?.average || 0}s</p>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-indigo-100">
                            <div className="flex items-center gap-2 mb-1">
                                <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                <h3 className="text-sm font-medium text-gray-500">Total Pharmacies</h3>
                            </div>
                            <p className="text-3xl font-bold text-indigo-600 mt-1">{chatOverview?.pharmacyCount ?? '—'}</p>
                        </div>
                    </div>

                    {/* Error Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <div className="flex justify-between items-start mb-2">
                                <h3 className="text-sm font-medium text-gray-500">Total Errors</h3>
                                <button
                                    onClick={() => navigate('/analytics/errors')}
                                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
                                >
                                    View Details
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            </div>
                            <div className="flex items-center mt-2">
                                <p className="text-3xl font-bold text-gray-900">{chatOverview?.errors?.total || 0}</p>
                                <span className="ml-2 text-sm text-red-500 bg-red-50 px-2 py-1 rounded-full">
                                    {chatOverview?.errors?.rate || 0}% Rate
                                </span>
                            </div>
                            <p className="text-xs text-gray-400 mt-2">Percentage of messages resulting in errors</p>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                            <div>
                                <h3 className="text-sm font-medium text-gray-500">System Health</h3>
                                <p className="text-xl font-bold text-gray-900 mt-1">
                                    {(chatOverview?.errors?.rate || 0) < 1 ? 'Excellent' :
                                        (chatOverview?.errors?.rate || 0) < 5 ? 'Good' : 'Needs Attention'}
                                </p>
                            </div>
                            <div className={`p-3 rounded-full ${(chatOverview?.errors?.rate || 0) < 1 ? 'bg-green-100 text-green-600' :
                                (chatOverview?.errors?.rate || 0) < 5 ? 'bg-yellow-100 text-yellow-600' : 'bg-red-100 text-red-600'
                                }`}>
                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    {/* Source Stats Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-bold text-gray-900">User Engagement by Source</h2>
                                <button
                                    onClick={() => {
                                        const params = new URLSearchParams();
                                        if (dateRange.startDate) params.append('startDate', dateRange.startDate);
                                        if (dateRange.endDate) params.append('endDate', dateRange.endDate);
                                        navigate(`/analytics/details?${params.toString()}`);
                                    }}
                                    className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                                >
                                    View Details →
                                </button>
                            </div>
                            <div className="space-y-4">
                                {sourceStats?.sourceStats?.map((stat, index) => (
                                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                        <div className="flex items-center space-x-3">
                                            <div className={`w-3 h-3 rounded-full ${stat.mode === 'Guide IA' ? 'bg-blue-500' : 'bg-green-500'}`}></div>
                                            <span className="font-medium text-gray-700">{stat.mode}</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <span className="text-xl font-bold text-gray-900">{stat.userCount}</span>
                                            <span className="text-sm text-gray-500">users</span>
                                        </div>
                                    </div>
                                ))}
                                {(!sourceStats?.sourceStats || sourceStats.sourceStats.length === 0) && (
                                    <p className="text-gray-500 text-center py-4">No source data available</p>
                                )}
                            </div>
                        </div>

                        {/* Feedback Stats */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <h2 className="text-lg font-bold text-gray-900 mb-4">Feedback Overview</h2>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                                    <div className="flex items-center justify-between">
                                        <span className="text-green-700 font-medium">Likes</span>
                                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                                        </svg>
                                    </div>
                                    <p className="text-2xl font-bold text-green-800 mt-2">{chatOverview?.feedback?.likes || 0}</p>
                                </div>
                                <div className="p-4 bg-red-50 rounded-lg border border-red-100">
                                    <div className="flex items-center justify-between">
                                        <span className="text-red-700 font-medium">Dislikes</span>
                                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.095c.5 0 .905-.405.905-.905 0-.714.211-1.412.608-2.006L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                                        </svg>
                                    </div>
                                    <p className="text-2xl font-bold text-red-800 mt-2">{chatOverview?.feedback?.dislikes || 0}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Messages by Input Type */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <h2 className="text-lg font-bold text-gray-900 mb-6">Messages par Type d'Entrée</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Guide IA */}
                            <div className="p-5 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="p-2 bg-blue-500 rounded-lg">
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                        </svg>
                                    </div>
                                    <h3 className="font-bold text-blue-900">Guide IA</h3>
                                </div>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                                        <div className="flex items-center gap-2">
                                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                            </svg>
                                            <span className="text-sm font-medium text-gray-700">Chat</span>
                                        </div>
                                        <span className="text-lg font-bold text-blue-900">{chatOverview?.modeDistribution?.guideByType?.text || 0}</span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                                        <div className="flex items-center gap-2">
                                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                            </svg>
                                            <span className="text-sm font-medium text-gray-700">Voice</span>
                                        </div>
                                        <span className="text-lg font-bold text-blue-900">{chatOverview?.modeDistribution?.guideByType?.voice || 0}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Agent Gestion */}
                            <div className="p-5 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100">
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="p-2 bg-green-500 rounded-lg">
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    </div>
                                    <h3 className="font-bold text-green-900">Agent Gestion</h3>
                                </div>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                                        <div className="flex items-center gap-2">
                                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                            </svg>
                                            <span className="text-sm font-medium text-gray-700">Chat</span>
                                        </div>
                                        <span className="text-lg font-bold text-green-900">{chatOverview?.modeDistribution?.agentByType?.text || 0}</span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                                        <div className="flex items-center gap-2">
                                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                            </svg>
                                            <span className="text-sm font-medium text-gray-700">Voice</span>
                                        </div>
                                        <span className="text-lg font-bold text-green-900">{chatOverview?.modeDistribution?.agentByType?.voice || 0}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Detailed Stats */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <h2 className="text-lg font-bold text-gray-900 mb-6">Detailed Statistics</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {/* Mode Distribution */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                                        </svg>
                                    </div>
                                    <h4 className="font-semibold text-gray-900">Mode Distribution</h4>
                                </div>

                                {(() => {
                                    const guideCount = chatOverview?.modeDistribution?.guide || 0;
                                    const agentCount = chatOverview?.modeDistribution?.agent || 0;
                                    const totalModes = guideCount + agentCount;
                                    const guidePercent = totalModes > 0 ? Math.round((guideCount / totalModes) * 100) : 0;
                                    const agentPercent = totalModes > 0 ? Math.round((agentCount / totalModes) * 100) : 0;

                                    return (
                                        <div className="space-y-4">
                                            <div>
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="text-gray-600">Guide Mode</span>
                                                    <span className="font-medium">{guideCount} ({guidePercent}%)</span>
                                                </div>
                                                <div className="w-full bg-gray-100 rounded-full h-2">
                                                    <div className="bg-indigo-500 h-2 rounded-full" style={{ width: `${guidePercent}%` }}></div>
                                                </div>
                                            </div>
                                            <div>
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="text-gray-600">Agent Mode</span>
                                                    <span className="font-medium">{agentCount} ({agentPercent}%)</span>
                                                </div>
                                                <div className="w-full bg-gray-100 rounded-full h-2">
                                                    <div className="bg-purple-500 h-2 rounded-full" style={{ width: `${agentPercent}%` }}></div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Sender Distribution */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                        </svg>
                                    </div>
                                    <h4 className="font-semibold text-gray-900">Sender Distribution</h4>
                                </div>

                                {(() => {
                                    const userMsgs = chatOverview?.senderDistribution?.user || 0;
                                    const agentMsgs = chatOverview?.senderDistribution?.agent || 0;
                                    const totalMsgs = userMsgs + agentMsgs;
                                    const userPercent = totalMsgs > 0 ? Math.round((userMsgs / totalMsgs) * 100) : 0;
                                    const agentPercent = totalMsgs > 0 ? Math.round((agentMsgs / totalMsgs) * 100) : 0;

                                    return (
                                        <div className="space-y-4">
                                            <div>
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="text-gray-600">User Messages</span>
                                                    <span className="font-medium">{userMsgs} ({userPercent}%)</span>
                                                </div>
                                                <div className="w-full bg-gray-100 rounded-full h-2">
                                                    <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${userPercent}%` }}></div>
                                                </div>
                                            </div>
                                            <div>
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="text-gray-600">Agent Responses</span>
                                                    <span className="font-medium">{agentMsgs} ({agentPercent}%)</span>
                                                </div>
                                                <div className="w-full bg-gray-100 rounded-full h-2">
                                                    <div className="bg-teal-500 h-2 rounded-full" style={{ width: `${agentPercent}%` }}></div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Avg Response Time per Mode */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="p-2 bg-orange-50 rounded-lg text-orange-600">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <h4 className="font-semibold text-gray-900">Temps de réponse moyen</h4>
                                </div>
                                <div className="space-y-3">
                                    {[
                                        { key: 'guide', label: 'Guide IA', bg: 'bg-blue-50', border: 'border-blue-100', text: 'text-blue-800', val: 'text-blue-900' },
                                        { key: 'agent', label: 'Agent Gestion', bg: 'bg-green-50', border: 'border-green-100', text: 'text-green-800', val: 'text-green-900' },
                                    ].map(({ key, label, bg, border, text, val }) => {
                                        const rt = chatOverview?.responseTimeByMode?.[key];
                                        return (
                                            <div key={key} className={`p-3 ${bg} rounded-lg border ${border}`}>
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className={`text-sm font-medium ${text}`}>{label}</span>
                                                    <span className={`text-xl font-bold ${val}`}>
                                                        {rt != null ? `${rt.avg}s` : '—'}
                                                    </span>
                                                </div>
                                                {rt != null && (
                                                    <div className="flex items-center justify-between text-xs text-gray-500">
                                                        <span>{rt.count} réponses</span>
                                                        <span>P95 ≈ {rt.p95}s</span>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center justify-between">
                                        <span className="text-xs text-gray-500 uppercase tracking-wide">Global</span>
                                        <span className="text-lg font-bold text-indigo-600">{(chatOverview?.responseTime?.average || 0)}s</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Feedback per Mode */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-bold text-gray-900">Satisfaction par Mode</h2>
                            <button
                                onClick={() => navigate('/feedbacks')}
                                className="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
                            >
                                View Details
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {[{ key: 'guide', label: 'Guide IA', color: 'blue' }, { key: 'agent', label: 'Agent Gestion', color: 'green' }].map(({ key, label, color }) => {
                                const fb = chatOverview?.feedbackByMode?.[key] || { likes: 0, dislikes: 0 };
                                const total = fb.likes + fb.dislikes;
                                const rate = total > 0 ? Math.round((fb.likes / total) * 100) : 0;
                                const barColor = rate >= 70 ? 'bg-green-500' : rate >= 40 ? 'bg-yellow-400' : 'bg-red-500';
                                const rateColor = rate >= 70 ? 'text-green-600' : rate >= 40 ? 'text-yellow-600' : 'text-red-600';
                                return (
                                    <div key={key} className={`p-5 bg-gradient-to-br from-${color}-50 to-${color === 'blue' ? 'indigo' : 'emerald'}-50 rounded-xl border border-${color}-100`}>
                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className={`font-bold text-${color}-900`}>{label}</h3>
                                            <span className={`text-2xl font-bold ${rateColor}`}>{rate}%</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
                                            <div className={`h-2.5 rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${rate}%` }}></div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                                                <div className="flex items-center gap-2">
                                                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                                                    </svg>
                                                    <span className="text-sm text-gray-600">Likes</span>
                                                </div>
                                                <span className="text-lg font-bold text-green-700">{fb.likes}</span>
                                            </div>
                                            <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                                                <div className="flex items-center gap-2">
                                                    <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.095c.5 0 .905-.405.905-.905 0-.714.211-1.412.608-2.006L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                                                    </svg>
                                                    <span className="text-sm text-gray-600">Dislikes</span>
                                                </div>
                                                <span className="text-lg font-bold text-red-700">{fb.dislikes}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Charts Section */}
                    {chartData && (
                        <>
                            {/* Messages Over Time Chart */}
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                                <h2 className="text-lg font-bold text-gray-900 mb-4">Messages Over Time</h2>
                                <ResponsiveContainer width="100%" height={300}>
                                    <LineChart data={chartData.messagesOverTime}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Line type="monotone" dataKey="total" stroke="#8884d8" strokeWidth={2} name="Total Messages" />
                                        <Line type="monotone" dataKey="guide" stroke="#82ca9d" strokeWidth={2} name="Guide" />
                                        <Line type="monotone" dataKey="agent" stroke="#ffc658" strokeWidth={2} name="Agent" />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Active Users Chart */}
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                                <h2 className="text-lg font-bold text-gray-900 mb-4">Active Users Per Day</h2>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={chartData.activeUsersPerDay}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="activeUsers" fill="#8884d8" name="Active Users" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Error Trends and Mode Distribution */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Error Trends Chart */}
                                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                                    <h2 className="text-lg font-bold text-gray-900 mb-4">Error Trends</h2>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <LineChart data={chartData.errorTrends}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="date" />
                                            <YAxis />
                                            <Tooltip />
                                            <Legend />
                                            <Line type="monotone" dataKey="errorCount" stroke="#ff6b6b" strokeWidth={2} name="Errors" />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>

                                {/* Mode Distribution Pie Chart */}
                                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                                    <h2 className="text-lg font-bold text-gray-900 mb-4">Mode Distribution</h2>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart>
                                            <Pie
                                                data={[
                                                    { name: 'Guide', value: chatOverview?.modeDistribution?.guide || 0 },
                                                    { name: 'Agent', value: chatOverview?.modeDistribution?.agent || 0 }
                                                ]}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                dataKey="value"
                                            >
                                                <Cell fill="#82ca9d" />
                                                <Cell fill="#ffc658" />
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>

    );
};

export default Analytics;
